import { useEffect, useState, useRef } from 'react'
import { lumi } from '@/lib/lumi'
import {Plus, Loader2, Pencil, Trash2, Upload, X, UserCircle2, GripVertical, ToggleLeft, ToggleRight, Save} from 'lucide-react'
import toast from 'react-hot-toast'
import type { TeamMember } from '@/types'
import { useAuth, canEdit, canDelete } from '@/hooks/useAuth'

const TEAL    = '#1A56DB'   // azul principal — paleta SISMEDI
const TEAL_BG = '#EFF6FF'   // azul ultra-leve

const EMPTY_FORM = { name: '', role: '', photoUrl: '', isActive: true, order: 0 }

export default function ManageTeam() {
  const { user } = useAuth()
  const allowEdit   = canEdit(user, 'equipe')
  const allowDelete = canDelete(user, 'equipe')
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<TeamMember | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState('')
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)

  const fetchMembers = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.team_members.list({
        sort: { order: 1 },
        limit: 100,
      })
      setMembers(list as TeamMember[])
    } catch {
      toast.error('Erro ao carregar membros da equipe.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchMembers() }, [])

  const openNew = () => {
    setEditing(null)
    setForm({ ...EMPTY_FORM, order: members.length + 1 })
    setPhotoFile(null)
    setPhotoPreview('')
    setShowForm(true)
  }

  const openEdit = (member: TeamMember) => {
    setEditing(member)
    setForm({
      name: member.name,
      role: member.role,
      photoUrl: member.photoUrl || '',
      isActive: member.isActive,
      order: member.order,
    })
    setPhotoFile(null)
    setPhotoPreview(member.photoUrl || '')
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditing(null)
    setForm(EMPTY_FORM)
    setPhotoFile(null)
    setPhotoPreview('')
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { toast.error('Apenas imagens são aceitas.'); return }
    setPhotoFile(file)
    const reader = new FileReader()
    reader.onload = ev => setPhotoPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim() || !form.role.trim()) {
      toast.error('Nome e função são obrigatórios.')
      return
    }
    setSaving(true)
    try {
      let finalPhotoUrl = form.photoUrl

      // Upload da foto se houver nova selecionada
      if (photoFile) {
        setUploadingPhoto(true)
        try {
          const res = await lumi.tools.file.upload([photoFile])
          finalPhotoUrl = res?.[0]?.url || res?.[0]?.fileUrl || ''
        } catch {
          toast.error('Erro ao enviar foto. Tentando salvar sem ela.')
        } finally {
          setUploadingPhoto(false)
        }
      }

      // Invoca a função Deno (server-side) para contornar a restrição ADMIN_ONLY
      if (editing) {
        const result = await lumi.functions.invoke('createAdminUser', {
          method: 'POST',
          body: {
            action: 'team_update',
            memberId: editing._id,
            name: form.name.trim(),
            role: form.role.trim(),
            photoUrl: finalPhotoUrl,
            isActive: form.isActive,
            order: Number(form.order) || 1,
          },
        }) as any
        if (result?.error || result?.success === false) {
          throw new Error(result?.error || 'Erro ao actualizar colaborador.')
        }
        toast.success('Colaborador actualizado!')
      } else {
        const result = await lumi.functions.invoke('createAdminUser', {
          method: 'POST',
          body: {
            action: 'team_create',
            name: form.name.trim(),
            role: form.role.trim(),
            photoUrl: finalPhotoUrl,
            isActive: form.isActive,
            order: Number(form.order) || 1,
            creator: user?.userId || 'admin',
          },
        }) as any
        if (result?.error || result?.success === false) {
          throw new Error(result?.error || 'Erro ao adicionar colaborador.')
        }
        toast.success('Colaborador adicionado!')
      }

      closeForm()
      fetchMembers()
    } catch (err: any) {
      toast.error(err?.data?.error || err?.message || 'Erro ao salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (member: TeamMember) => {
    if (!confirm(`Remover "${member.name}" da equipe?`)) return
    setDeleting(member._id)
    try {
      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: { action: 'team_delete', memberId: member._id },
      }) as any
      if (result?.error || result?.success === false) {
        throw new Error(result?.error || 'Erro ao remover colaborador.')
      }
      toast.success('Colaborador removido.')
      fetchMembers()
    } catch (err: any) {
      toast.error(err?.data?.error || err?.message || 'Erro ao remover.')
    } finally {
      setDeleting(null)
    }
  }

  const handleToggleActive = async (member: TeamMember) => {
    try {
      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: {
          action: 'team_update',
          memberId: member._id,
          isActive: !member.isActive,
        },
      }) as any
      if (result?.error || result?.success === false) throw new Error(result?.error)
      setMembers(prev => prev.map(m => m._id === member._id ? { ...m, isActive: !m.isActive } : m))
    } catch {
      toast.error('Erro ao actualizar status.')
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Nossa Equipe</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Gerencie os colaboradores exibidos na secção "Nossa Equipe" do site.
          </p>
        </div>
        {allowEdit && (
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-sm transition-all"
            style={{ background: TEAL }}
            onMouseEnter={e => (e.currentTarget.style.background = '#1447C0')}
            onMouseLeave={e => (e.currentTarget.style.background = TEAL)}
          >
            <Plus size={16} />
            Novo Colaborador
          </button>
        )}
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Total', value: members.length, color: TEAL },
          { label: 'Ativos', value: members.filter(m => m.isActive).length, color: '#22c55e' },
          { label: 'Inativos', value: members.filter(m => !m.isActive).length, color: '#9ca3af' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 text-center">
            <div className="text-2xl font-black mb-0.5" style={{ color }}>{value}</div>
            <div className="text-xs text-gray-400 font-medium">{label}</div>
          </div>
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin" style={{ color: TEAL }} />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white dark:bg-gray-900">
          <UserCircle2 size={40} className="mx-auto mb-3" style={{ color: '#d1d5db' }} />
          <p className="text-gray-400 text-sm">Nenhum colaborador cadastrado ainda.</p>
          {allowEdit && (
            <button
              onClick={openNew}
              className="mt-4 px-5 py-2 rounded-xl text-sm font-bold text-white"
              style={{ background: TEAL }}
            >
              Adicionar primeiro colaborador
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {members.map(member => (
            <div
              key={member._id}
              className={`flex items-center gap-4 bg-white dark:bg-gray-900 rounded-2xl border p-4 transition-all ${
                member.isActive ? 'border-gray-100 dark:border-gray-800' : 'border-gray-100 dark:border-gray-800 opacity-60'
              }`}
            >
              <GripVertical size={16} className="text-gray-300 flex-shrink-0 cursor-grab" />

              {/* Foto */}
              <div
                className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center"
                style={{ background: TEAL_BG }}
              >
                {member.photoUrl ? (
                  <img src={member.photoUrl} alt={member.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-lg font-black" style={{ color: TEAL }}>
                    {member.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* Dados */}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900 dark:text-white text-sm truncate">{member.name}</div>
                <div className="text-xs text-gray-400 truncate">{member.role}</div>
              </div>

              {/* Ordem */}
              <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
                <span className="text-xs text-gray-400">Ordem:</span>
                <span className="text-xs font-bold text-gray-600 dark:text-gray-300">{member.order}</span>
              </div>

              {/* Toggle ativo */}
              <button
                onClick={() => handleToggleActive(member)}
                className="flex-shrink-0 p-1 transition-colors"
                title={member.isActive ? 'Desativar' : 'Ativar'}
              >
                {member.isActive
                  ? <ToggleRight size={22} style={{ color: TEAL }} />
                  : <ToggleLeft size={22} className="text-gray-300" />
                }
              </button>

              {/* Acções */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {allowEdit && (
                  <button
                    onClick={() => openEdit(member)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    title="Editar"
                  >
                    <Pencil size={14} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" />
                  </button>
                )}
                {allowDelete && (
                  <button
                    onClick={() => handleDelete(member)}
                    disabled={deleting === member._id}
                    className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950 transition-colors disabled:opacity-50"
                    title="Remover"
                  >
                    {deleting === member._id
                      ? <Loader2 size={14} className="text-red-400 animate-spin" />
                      : <Trash2 size={14} className="text-gray-400 hover:text-red-500" />
                    }
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de formulário */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fade-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {editing ? 'Editar Colaborador' : 'Novo Colaborador'}
              </h2>
              <button
                onClick={closeForm}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              {/* Foto */}
              <div className="flex flex-col items-center">
                <div
                  className="relative w-24 h-24 rounded-full overflow-hidden cursor-pointer mb-2 flex items-center justify-center border-2 border-dashed transition-all"
                  style={{ borderColor: TEAL, background: photoPreview ? 'transparent' : TEAL_BG }}
                  onClick={() => photoInputRef.current?.click()}
                >
                  {photoPreview ? (
                    <>
                      <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity rounded-full">
                        <Upload size={18} className="text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      {uploadingPhoto
                        ? <Loader2 size={20} className="animate-spin" style={{ color: TEAL }} />
                        : <Upload size={20} style={{ color: TEAL }} />
                      }
                      <span className="text-xs font-bold" style={{ color: TEAL }}>FOTO</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400">Clique para adicionar/trocar a foto <span className="text-gray-300">(opcional)</span></p>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
                {photoPreview && (
                  <button
                    type="button"
                    onClick={() => { setPhotoFile(null); setPhotoPreview(''); setForm(p => ({ ...p, photoUrl: '' })) }}
                    className="mt-1 text-xs text-red-400 hover:text-red-600 transition-colors"
                  >
                    Remover foto
                  </button>
                )}
              </div>

              {/* Nome */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Nome completo *
                </label>
                <input
                  type="text"
                  placeholder="Ex: Maria Silva"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none transition-colors"
                  onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                  onBlur={e => (e.currentTarget.style.boxShadow = '')}
                />
              </div>

              {/* Função */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Função / Cargo *
                </label>
                <input
                  type="text"
                  placeholder="Ex: Fisioterapeuta, Enfermeira, Cozinheira..."
                  value={form.role}
                  onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none transition-colors"
                  onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                  onBlur={e => (e.currentTarget.style.boxShadow = '')}
                />
              </div>

              {/* Ordem e Ativo */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Ordem de exibição
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={form.order}
                    onChange={e => setForm(p => ({ ...p, order: Number(e.target.value) }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none transition-colors"
                    onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                    onBlur={e => (e.currentTarget.style.boxShadow = '')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Status
                  </label>
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, isActive: !p.isActive }))}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all"
                    style={form.isActive
                      ? { borderColor: TEAL, color: TEAL, background: TEAL_BG }
                      : { borderColor: '#d1d5db', color: '#9ca3af', background: '#f9fafb' }
                    }
                  >
                    {form.isActive
                      ? <><ToggleRight size={16} /> Ativo</>
                      : <><ToggleLeft size={16} /> Inativo</>
                    }
                  </button>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving || uploadingPhoto}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60 transition-all"
                  style={{ background: TEAL }}
                >
                  {saving || uploadingPhoto
                    ? <><Loader2 size={14} className="animate-spin" /> Salvando...</>
                    : <><Save size={14} /> Salvar</>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
